import { Store } from 'vuex'
import { Router } from 'vue-router'
import { AxiosError, AxiosInstance } from 'axios'
import { instanceAuth } from '@/model/api/apibase'
import { State as StoreState } from '@/store/index'
import API_URL from '@/model/api/apiUrlType'
import * as log from '@/model/util/log/Logger'
import { ErrorResponse } from '@/model/api/error/erroResponse'
import { useApiError } from '@/model/api/error/apiError'
import { ErrorMsgState } from '@/model/components/common/message/errorMessage'

// ダウンロード検索
// ダウンロードキー検索リクエストデータ
type SearchRequestData = {
  // ダウンロードキー
  key: string
}

// 検索結果メール添付ファイルレスポンスデータ
type SearchAttachedResponseData = {
  // ファイル名
  name: string
  // ファイルサイズ
  size: number
}

// 検索結果メール添付ファイルレスポンスデータ
type SearchResponseData = {
  // 送信日時
  senddate: string
  // 差出人
  from: string
  // 件名
  subject: string
  // メールサイズ（バイト数）
  size: number
  // 添付ファイル一覧
  attached: SearchAttachedResponseData[]
  // 添付ファイルボタン表示有無
  dispAttached: boolean
  // emlボタン表示有無
  dispEml: boolean
}

// ダウンロード実行キー生成
// ダウンロード実行キー生成リクエストデータ
type CreateRequestData = {
  // ダウンロード種別
  type: string
}

// ダウンロード実行キー生成レスポンスデータ
type CreateResponseData = {
  // ダウンロード実行キー
  downloadexekey: string
}

// ダウンロード種別定義
const DOWNLOAD_TYPE = {
  MAIL: 'eml', // メールダウンロード
  FILE: 'file', // 添付ファイルダウンロード
} as const
type DOWNLOAD_TYPE = typeof DOWNLOAD_TYPE[keyof typeof DOWNLOAD_TYPE]

// ダウンロード準備処理
// ダウンロード準備リクエストデータ
type PrepareRequestData = {
  // ダウンロード実行キー
  downloadexekey: string
}
// ダウンロード準備レスポンスデータ
type PrepareResponseData = {
  // ファイルダウンロードurl
  url: string
  // ファイルダウンロード前スリープミリ秒
  sleep: number
}

// ダウンロード後処理
// ダウンロード後処理リクエストデータ
type PostProcessRequestData = {
  // ダウンロード実行キー
  downloadexekey: string
  // ダウンロード実行ステータス
  status: string
}

// ダウンロード監視処理
// ダウンロード監視リクエストデータ
type StatusRequestData = {
  // ダウンロード実行キー
  downloadexekey: string
}

// ダダウンロード監視レスポンスデータ
type StatusResponseData = {
  // ダウンロード状態
  status: number
}

// ダウンロード種別定義
const DOWNLOAD_STATUS = {
  INIT: 0, // 初期状態
  GATEWAY_REQUEST: 1, // gatewayにリクエスト
  GATEWAY_COMPLETE: 2, // gatewayからアップロード完了
  COMPLETE: 3, // ブラウザへのダウンロード準備完了
  ERROR: 4, // 異常
  SUCCESS: 5, // 成功
  FAILED: 6, // 失敗
} as const
type DOWNLOAD_STATUS = typeof DOWNLOAD_STATUS[keyof typeof DOWNLOAD_STATUS]

/**
 * ダウンロードAPI関連の関数を返す
 * @param router vue router
 * @param store vuex
 * @returns ダウンロードAPI関連の関数
 */
const userDownload = (router: Router, store: Store<StoreState>) => {
  // API通信インスタンス
  const instance: AxiosInstance | undefined = instanceAuth(router, store)

  /**
   * ダウンロードキー検索
   * @param key ダウンロードキー
   * @param error エラー情報
   * @returns 取得データ(エラーの場合はundefined)
   */
  const search = async (key: string, error: ErrorMsgState) => {
    // 戻り値
    let normaldata: SearchResponseData | undefined
    // リクエストデータ設定
    const request: SearchRequestData = {
      key,
    }
    // 認証後のaxiosインスタンス取得
    const obj = instance
    await obj
      .get<SearchResponseData>(
        API_URL.USERS.DOWNLOAD, // URL
        {
          params: request, // リクエストデータ
        }
      )
      .then((response) => {
        // 正常に取得できた場合はそのまま返す
        normaldata = response.data
      })
      .catch(async (e: AxiosError<ErrorResponse>) => {
        // 異常時はエラーメッセージを設定する
        await useApiError().setbaseLinkingError(e, error)
      })
    return {
      normaldata,
    }
  }
  /**
   * ダウンロード実行キー生成
   * @param key ダウンロードキー
   * @param type ダウンロード種別
   * @param error エラー情報
   * @returns 取得データ(エラーの場合はundefined)
   */
  const create = async (key: string, type: string, error: ErrorMsgState) => {
    // 戻り値
    let normaldata: CreateResponseData | undefined
    // リクエストデータ設定
    const request: CreateRequestData = {
      type,
    }
    // 認証後のaxiosインスタンス取得
    const obj = instance
    await obj
      .post<CreateResponseData>(
        `${API_URL.USERS.DOWNLOAD}/${key}`, // URL
        request // リクエストデータ
      )
      .then((response) => {
        // 正常に取得できた場合はそのまま返す
        normaldata = response.data
      })
      .catch(async (e: AxiosError<ErrorResponse>) => {
        // 異常時はエラーメッセージを設定する
        await useApiError().setbaseError(e, error)
      })
    return {
      normaldata,
    }
  }
  /**
   * ダウンロード準備
   * @param key ダウンロードキー
   * @param exekey ダウンロード実行キー
   * @param error エラー情報
   * @returns 取得データ(エラーの場合はundefined)
   */
  const prepare = async (key: string, exekey: string, error: ErrorMsgState) => {
    // 戻り値
    // ダウンロード用バイナリデータ
    let normaldata: PrepareResponseData | undefined
    // リクエストデータ設定
    const request: PrepareRequestData = {
      downloadexekey: exekey,
    }
    // 認証後のaxiosインスタンス取得
    const obj = instance
    await obj
      .post<PrepareResponseData>(
        `${API_URL.USERS.DOWNLOAD}/${key}/prepare`, // URL
        request // リクエストデータ
      )
      .then((response) => {
        // 正常に取得できた場合はそのまま返す
        normaldata = response.data
      })
      .catch(async (e: AxiosError<ErrorResponse>) => {
        // 異常時はエラーメッセージを設定する
        await useApiError().setbaseError(e, error)
      })
    return {
      normaldata,
    }
  }
  /**
   * ダウンロード後処理
   * @param key ダウンロードキー
   * @param exekey ダウンロード実行キー
   * @param status ダウンロード実行ステータス
   */
  const postProcessDownload = async (key: string, exekey: string, status: DOWNLOAD_STATUS) => {
    // リクエストデータ設定
    const request: PostProcessRequestData = {
      downloadexekey: exekey,
      status: status.toString(),
    }
    // 認証後のaxiosインスタンス取得
    const obj = instance
    await obj
      .post(
        `${API_URL.USERS.DOWNLOAD}/${key}/complete`, // URL
        request // リクエストデータ
      )
      .catch(async (e: AxiosError<ErrorResponse>) => {
        // 異常時は何もしない
        log.debug(`download post process failed. ${e}`)
      })
    // エラー処理は行わないので投げっぱなし
  }
  /**
   * ダウンロード監視
   * @param key ダウンロードキー
   * @param exekey ダウンロード実行キー
   * @param error エラー情報
   * @returns 取得データ(エラーの場合はundefined)
   */
  const status = async (key: string, exekey: string, error: ErrorMsgState) => {
    // 戻り値
    let normaldata: StatusResponseData | undefined
    // リクエストデータ設定
    const request: StatusRequestData = {
      downloadexekey: exekey,
    }
    // 認証後のaxiosインスタンス取得
    const obj = instance
    await obj
      .get<StatusResponseData>(
        `${API_URL.USERS.DOWNLOAD}/${key}/status`, // URL
        {
          params: request, // リクエストデータ
        }
      )
      .then((response) => {
        // 正常に取得できた場合はそのまま返す
        normaldata = response.data
      })
      .catch(async (e: AxiosError<ErrorResponse>) => {
        // 異常時はエラーメッセージを設定する
        await useApiError().setbaseError(e, error)
      })
    return {
      normaldata,
    }
  }

  return {
    instance,
    search,
    create,
    prepare,
    postProcessDownload,
    status,
  }
}

export { userDownload, DOWNLOAD_TYPE, DOWNLOAD_STATUS, PrepareResponseData }
